/**
 * Product Service - Production Ready
 *
 * Field Mapping:
 * Frontend -> Backend:
 * - name -> productName
 * - code -> productCode
 * - description -> productDescription
 * - status -> productStatus
 * - images -> productImages
 *
 * Category:
 * - name -> categoryName
 *
 * SubCategory:
 * - name -> subCategoryName
 */

import apiService from 'core/services/apiClient';
import logger from 'core/utils/logger';
import type {
  Product,
  ProductCategory,
  ProductSubCategory,
  ProductListParams,
  ProductListResponse,
  CategoryListParams,
  CategoryListResponse,
  CreateProductRequest,
  UpdateProductRequest,
  BulkProductOperation,
  ProductExportParams,
} from '../types';

// API path constants
const API_PATHS = {
  WEB_PRODUCTS: 'web/master/product/products',
  WEB_CATEGORIES: 'web/master/product/categories',
  WEB_SUB_CATEGORIES: 'web/master/product/sub-categories',
  // Import/Export endpoints (background jobs with email)
  IMPORT_EXPORT: 'web/master/product/import-export',
  // Image upload endpoint
  PRODUCT_IMAGES: 'web/master/product/products/images/upload',
  // Statistics endpoints
  STATISTICS: 'web/master/product/products/statistics',
  STATS: 'web/master/product/stats',
};

// Import/Export Types — must match backend enums in create-import-job.dto.ts / create-export-job.dto.ts
export type ProductImportType = 'all_products' | 'pricing_only';
export type ProductExportType = 'all_products' | 'active_products' | 'inactive_products' | 'by_category' | 'with_pricing';

export interface StartImportJobRequest {
  fileContent: string; // base64
  fileName: string;
  importType: ProductImportType;
  updateExisting: boolean;
  skipDuplicates: boolean;
  jobName: string;
}

export interface StartExportJobRequest {
  exportType: ProductExportType;
  format: 'excel' | 'csv';
  filters?: Record<string, string>;
  selectedFields: string[];
  jobName: string;
}

export interface ImportExportJobResponse {
  jobId: string;
  status: string;
  totalRows?: number;
  message?: string;
}

// ==================== FIELD TRANSFORMERS ====================

/**
 * Transform frontend product data to backend format
 */
const transformProductToApi = (data: any): any => {
  const apiData: any = {};

  // Map frontend field names to backend field names
  if (data.name !== undefined) apiData.productName = data.name;
  if (data.code !== undefined) apiData.productCode = data.code;
  if (data.description !== undefined) apiData.productDescription = data.description;
  if (data.status !== undefined) apiData.productStatus = data.status;

  // Direct mappings (same field names)
  if (data.categoryId !== undefined) apiData.categoryId = data.categoryId;
  if (data.subCategoryId !== undefined) apiData.subCategoryId = data.subCategoryId;
  // `sellingPrice` and state/customer pricing stay in the PriceManage module.
  // `mrp` is sent from here so a product can be given its base price at
  // creation — otherwise a hand-added product is stuck at 0 until someone
  // visits a second screen, which is how existing rows ended up at 0.00.
  // Every assignment here is guarded on `!== undefined`: an unguarded write
  // would send `undefined` for a tenant whose form has no such input, and the
  // backend's `Object.assign` in update() would then null out imported values.
  if (data.mrp !== undefined) apiData.mrp = data.mrp;
  if (data.colour !== undefined) apiData.colour = data.colour;
  if (data.smallPackingSize !== undefined) apiData.smallPackingSize = data.smallPackingSize;
  if (data.isActive !== undefined) apiData.isActive = data.isActive;
  if (data.unitOfMeasurement !== undefined) apiData.unitOfMeasurement = data.unitOfMeasurement;
  if (data.customFields !== undefined) apiData.customFields = data.customFields;
  if (data.metadata !== undefined) apiData.metadata = data.metadata;
  if (data.pointCategoryName !== undefined) apiData.pointCategoryName = data.pointCategoryName;
  if (data.productType !== undefined) apiData.productType = data.productType;
  if (data.primaryImageUrl !== undefined) apiData.primaryImageUrl = data.primaryImageUrl;

  // Handle images - transform to productImages format
  // API expects: url, thumbnailUrl, alt, displayOrder, isPrimary
  if (data.images && Array.isArray(data.images)) {
    // If images are File objects, they'll be handled separately in FormData
    // If images are URLs/objects, transform to productImages
    const nonFileImages = data.images.filter((img: any) => !(img instanceof File));
    if (nonFileImages.length > 0) {
      apiData.productImages = nonFileImages.map((img: any, index: number) => {
        if (typeof img === 'string') {
          return { url: img, displayOrder: index, isPrimary: index === 0 };
        }
        // Handle various URL property names: imageUrl (API GET), url (API PUT), file (frontend)
        const imageUrl = img.imageUrl || img.url || img.file || img;
        const result: any = {
          url: imageUrl,
          alt: img.altText || img.alt || img.title || '',
          displayOrder: img.displayOrder ?? index,
          isPrimary: img.isPrimary ?? index === 0,
        };
        // Preserve thumbnailUrl if available (important for edit mode)
        if (img.thumbnailUrl) {
          result.thumbnailUrl = img.thumbnailUrl;
        }
        // Preserve image ID if available (for existing images)
        if (img.id) {
          result.id = img.id;
        }
        return result;
      });
    }
  }

  return apiData;
};

/**
 * Transform backend product data to frontend format
 * Note: Price/discount fields are managed in separate modules but included for view purposes
 */
const transformProductFromApi = (data: any): Product => {
  return {
    id: data.id,
    // Basic fields
    name: data.productName || data.name || '',
    code: data.productCode || data.code || '',
    description: data.productDescription || data.description || '',
    categoryId: data.categoryId,
    subCategoryId: data.subCategoryId,
    uom: data.uom || data.unitOfMeasurement || 'Unit',
    // Status
    status: data.productStatus || data.status || 'active',
    isActive: data.isActive ?? true,
    // Product Type (simple or variant)
    productType: data.productType || 'simple',
    // Media - API returns productImages with url or imageUrl property
    images: data.productImages || data.images || [],
    primaryImageUrl: data.primaryImageUrl || data.productImages?.[0]?.url || data.productImages?.[0]?.imageUrl || null,
    // Pricing (from price manage module)
    mrp: data.mrp || 0,
    sellingPrice: data.sellingPrice || data.mrp || 0,
    // Product attributes. This function builds a NEW object from a fixed set
    // of keys rather than spreading `data`, so a field absent from this list
    // is dropped before any component sees it — which is why these values
    // were invisible in the UI despite the backend returning them.
    colour: data.colour ?? '',
    smallPackingSize: data.smallPackingSize ?? '',
    // Brand
    brandName: data.brandName || '',
    // Relations
    category: data.category,
    subCategory: data.subCategory,
    // Category/SubCategory names (backend uses categoryName, subCategoryName)
    categoryName: data.category?.categoryName || '',
    subCategoryName: data.subCategory?.subCategoryName || '',
    // Custom fields
    customFields: data.customFields || data.metadata || {},
    metadata: data.metadata || {},
    // Loyalty
    pointCategoryName: data.pointCategoryName || '',
    // Timestamps
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  } as any;
};

/**
 * Transform frontend category data to backend format
 */
const transformCategoryToApi = (data: any): any => {
  const apiData: any = {};

  if (data.name !== undefined) apiData.categoryName = data.name;
  if (data.description !== undefined) apiData.description = data.description;
  if (data.image !== undefined) apiData.imageUrl = data.image;
  if (data.isActive !== undefined) apiData.isActive = data.isActive;
  if (data.metadata !== undefined) apiData.metadata = data.metadata;

  return apiData;
};

/**
 * Transform backend category data to frontend format
 */
const transformCategoryFromApi = (data: any): ProductCategory => {
  return {
    id: data.id,
    name: data.categoryName || data.name || '',
    description: data.categoryDescription || data.description || '',
    image: data.categoryImage || data.imageUrl || data.image || '',
    isActive: data.isActive ?? true,
    productCount: data.productCount || 0,
    // Counted server-side by getCategory; list rows don't carry it.
    subCategoryCount: data.subCategoryCount ?? 0,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
};

/**
 * Transform frontend subcategory data to backend format
 */
const transformSubCategoryToApi = (data: any): any => {
  const apiData: any = {};

  if (data.name !== undefined) apiData.subCategoryName = data.name;
  if (data.categoryId !== undefined) apiData.categoryId = data.categoryId;
  if (data.description !== undefined) apiData.description = data.description;
  // `imageUrl` is the DTO's name for it; the backend maps it onto the
  // sub_category_image column. Matches how categories send their image.
  if (data.image !== undefined) apiData.imageUrl = data.image;
  if (data.isActive !== undefined) apiData.isActive = data.isActive;
  if (data.metadata !== undefined) apiData.metadata = data.metadata;

  return apiData;
};

/**
 * Transform backend subcategory data to frontend format
 */
const transformSubCategoryFromApi = (data: any): ProductSubCategory => {
  return {
    id: data.id,
    name: data.subCategoryName || data.name || '',
    categoryId: data.categoryId,
    description: data.subCategoryDescription || data.description || '',
    image: data.subCategoryImage || data.image || '',
    isActive: data.isActive ?? true,
    category: data.category ? {
      id: data.category.id,
      name: data.category.categoryName || data.category.name || '',
    } : undefined,
    productCount: data.productCount || 0,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
};

class ProductService {
  // ==================== CATEGORY MANAGEMENT ====================

  async getCategories(params?: CategoryListParams): Promise<CategoryListResponse> {
    try {
      logger.info('Fetching product categories', { params });
      const response = await apiService.get(API_PATHS.WEB_CATEGORIES, { params });

      // apiClient interceptor already unwraps { statusCode, status, data } to just data
      // So response is either the data array directly, or { data, pagination } for paginated responses
      const rawData = Array.isArray(response) ? response : (response?.data || response || []);
      const transformedData = Array.isArray(rawData)
        ? rawData.map(transformCategoryFromApi)
        : [];

      // Pagination can be in response.pagination (from interceptor) or response.meta?.pagination
      const pagination = response?.pagination || response?.meta?.pagination || {};

      return {
        data: transformedData,
        total: pagination.total || transformedData.length,
        page: pagination.page || params?.page || 1,
        limit: pagination.limit || params?.limit || 10,
        totalPages: pagination.totalPages || Math.ceil((pagination.total || transformedData.length) / (params?.limit || 10)),
      };
    } catch (error) {
      logger.error('Failed to fetch categories', error);
      throw error;
    }
  }

  async getCategoryById(id: string | number): Promise<ProductCategory> {
    try {
      logger.info('Fetching category detail', { id });
      const response = await apiService.get(`${API_PATHS.WEB_CATEGORIES}/${id}`);
      const data = response?.data || response;
      return transformCategoryFromApi(data);
    } catch (error) {
      logger.error('Failed to fetch category detail', error);
      throw error;
    }
  }

  async createCategory(data: Partial<ProductCategory>): Promise<ProductCategory> {
    try {
      logger.info('Creating category', { data });
      const apiData = transformCategoryToApi(data);
      const response = await apiService.post(API_PATHS.WEB_CATEGORIES, apiData);
      const result = response?.data || response;
      return transformCategoryFromApi(result);
    } catch (error) {
      logger.error('Failed to create category', error);
      throw error;
    }
  }

  async updateCategory(id: string | number, data: Partial<ProductCategory>): Promise<ProductCategory> {
    try {
      logger.info('Updating category', { id, data });
      const apiData = transformCategoryToApi(data);
      const response = await apiService.put(`${API_PATHS.WEB_CATEGORIES}/${id}`, apiData);
      const result = response?.data || response;
      return transformCategoryFromApi(result);
    } catch (error) {
      logger.error('Failed to update category', error);
      throw error;
    }
  }

  async deleteCategory(id: string | number): Promise<void> {
    try {
      logger.info('Deleting category', { id });
      await apiService.delete(`${API_PATHS.WEB_CATEGORIES}/${id}`);
    } catch (error) {
      logger.error('Failed to delete category', error);
      throw error;
    }
  }

  // ==================== SUB-CATEGORY MANAGEMENT ====================

  async getSubCategories(params?: { categoryId?: string | number; page?: number; limit?: number; includeCategory?: boolean }): Promise<{
    data: ProductSubCategory[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    try {
      logger.info('Fetching sub-categories', { params });
      const response = await apiService.get(API_PATHS.WEB_SUB_CATEGORIES, { params });

      // apiClient interceptor already unwraps { statusCode, status, data } to just data
      // So response is either the data array directly, or { data, pagination } for paginated responses
      const rawData = Array.isArray(response) ? response : (response?.data || response || []);
      const dataArray = Array.isArray(rawData) ? rawData : (rawData?.items || rawData?.list || []);

      const transformedData = dataArray.map(transformSubCategoryFromApi);

      // Pagination can be in response.pagination (from interceptor) or response.meta?.pagination
      const pagination = response?.pagination || response?.meta?.pagination || {};

      return {
        data: transformedData,
        total: pagination.total || transformedData.length,
        page: pagination.page || params?.page || 1,
        limit: pagination.limit || params?.limit || 10,
        totalPages: pagination.totalPages || Math.ceil((pagination.total || transformedData.length) / (params?.limit || 10)),
      };
    } catch (error) {
      logger.error('Failed to fetch sub-categories', error);
      return { data: [], total: 0, page: 1, limit: 10, totalPages: 0 };
    }
  }

  async getSubCategoryById(id: string | number): Promise<ProductSubCategory> {
    try {
      logger.info('Fetching sub-category detail', { id });
      const response = await apiService.get(`${API_PATHS.WEB_SUB_CATEGORIES}/${id}`);
      const data = response?.data || response;
      return transformSubCategoryFromApi(data);
    } catch (error) {
      logger.error('Failed to fetch sub-category detail', error);
      throw error;
    }
  }

  async createSubCategory(data: Partial<ProductSubCategory>): Promise<ProductSubCategory> {
    try {
      logger.info('Creating sub-category', { data });
      const apiData = transformSubCategoryToApi(data);
      const response = await apiService.post(API_PATHS.WEB_SUB_CATEGORIES, apiData);
      const result = response?.data || response;
      return transformSubCategoryFromApi(result);
    } catch (error) {
      logger.error('Failed to create sub-category', error);
      throw error;
    }
  }

  async updateSubCategory(id: string | number, data: Partial<ProductSubCategory>): Promise<ProductSubCategory> {
    try {
      logger.info('Updating sub-category', { id, data });
      const apiData = transformSubCategoryToApi(data);
      const response = await apiService.put(`${API_PATHS.WEB_SUB_CATEGORIES}/${id}`, apiData);
      const result = response?.data || response;
      return transformSubCategoryFromApi(result);
    } catch (error) {
      logger.error('Failed to update sub-category', error);
      throw error;
    }
  }

  async deleteSubCategory(id: string | number): Promise<void> {
    try {
      logger.info('Deleting sub-category', { id });
      await apiService.delete(`${API_PATHS.WEB_SUB_CATEGORIES}/${id}`);
    } catch (error) {
      logger.error('Failed to delete sub-category', error);
      throw error;
    }
  }

  // ==================== PRODUCT MANAGEMENT ====================

  async getProducts(params?: ProductListParams): Promise<ProductListResponse> {
    try {
      logger.info('Fetching products', { params });
      const response = await apiService.get(API_PATHS.WEB_PRODUCTS, { params });

      // apiClient interceptor already unwraps { statusCode, status, data } to just data
      // So response is either the data array directly, or { data, pagination } for paginated responses
      const rawData = Array.isArray(response) ? response : (response?.data || response || []);
      const transformedData = Array.isArray(rawData)
        ? rawData.map(transformProductFromApi)
        : [];

      // Pagination can be in response.pagination (from interceptor) or response.meta?.pagination
      const pagination = response?.pagination || response?.meta?.pagination || {};
      const total = pagination.total ?? transformedData.length;

      return {
        data: transformedData,
        total: total,
        page: pagination.page || params?.page || 1,
        limit: pagination.limit || params?.limit || 10,
        totalPages: pagination.totalPages || Math.ceil(total / (params?.limit || 10)),
      };
    } catch (error) {
      logger.error('Failed to fetch products', error);
      throw error;
    }
  }

  async getProductById(id: string | number): Promise<Product> {
    try {
      logger.info('Fetching product detail', { id });
      const response = await apiService.get(`${API_PATHS.WEB_PRODUCTS}/${id}`);
      const data = response?.data || response;
      return transformProductFromApi(data);
    } catch (error) {
      logger.error('Failed to fetch product detail', error);
      throw error;
    }
  }

  async createProduct(data: CreateProductRequest): Promise<Product> {
    try {
      logger.info('Creating product', { data });

      // Transform field names
      const apiData = transformProductToApi(data);

      // Handle file uploads using dedicated uploadFile method
      if (data.images && data.images.some((img) => img instanceof File)) {
        const formData = new FormData();

        // Add transformed data to formData
        Object.entries(apiData).forEach(([key, value]) => {
          if (key !== 'productImages' && value !== undefined && value !== null) {
            if (typeof value === 'object') {
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, String(value));
            }
          }
        });

        // Add file images
        data.images.forEach((image) => {
          if (image instanceof File) {
            formData.append('images', image);
          }
        });

        // Use uploadFile for proper FormData handling (no manual Content-Type)
        const response = await apiService.uploadFile(API_PATHS.WEB_PRODUCTS, formData);
        return transformProductFromApi(response?.data || response);
      }

      const response = await apiService.post(API_PATHS.WEB_PRODUCTS, apiData);
      return transformProductFromApi(response?.data || response);
    } catch (error) {
      logger.error('Failed to create product', error);
      throw error;
    }
  }

  async updateProduct(data: UpdateProductRequest): Promise<Product> {
    try {
      const { id, ...updateData } = data;
      logger.info('Updating product', { id, updateData });

      // Transform field names
      const apiData = transformProductToApi(updateData);

      // Handle file uploads using dedicated uploadFilePut method
      if (updateData.images && updateData.images.some((img) => img instanceof File)) {
        const formData = new FormData();

        Object.entries(apiData).forEach(([key, value]) => {
          if (key !== 'productImages' && value !== undefined && value !== null) {
            if (typeof value === 'object') {
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, String(value));
            }
          }
        });

        updateData.images.forEach((image) => {
          if (image instanceof File) {
            formData.append('images', image);
          }
        });

        // Use uploadFilePut for proper FormData handling with PUT method
        const response = await apiService.uploadFilePut(`${API_PATHS.WEB_PRODUCTS}/${id}`, formData);
        return transformProductFromApi(response?.data || response);
      }

      const response = await apiService.put(`${API_PATHS.WEB_PRODUCTS}/${id}`, apiData);
      return transformProductFromApi(response?.data || response);
    } catch (error) {
      logger.error('Failed to update product', error);
      throw error;
    }
  }

  async deleteProduct(id: string | number): Promise<void> {
    try {
      logger.info('Deleting product', { id });
      await apiService.delete(`${API_PATHS.WEB_PRODUCTS}/${id}`);
    } catch (error) {
      logger.error('Failed to delete product', error);
      throw error;
    }
  }

  // ==================== PRODUCT IMAGE OPERATIONS ====================

  /**
   * Upload product images
   * Uses dedicated uploadFile method to ensure proper FormData handling
   * @param files - Array of File objects to upload
   * @returns Array of uploaded image details with URLs
   */
  async uploadProductImages(files: File[]): Promise<{ url: string; fileName: string; originalName: string; fileSize: number; mimeType: string }[]> {
    try {
      if (!files || files.length === 0) {
        throw new Error('No files provided');
      }

      logger.info('Uploading product images', { count: files.length });

      const formData = new FormData();
      files.forEach((file) => {
        formData.append('images', file);
      });

      // Use uploadFile method (like AppConfig) for proper FormData handling
      const response = await apiService.uploadFile(API_PATHS.PRODUCT_IMAGES, formData);
      const images = response?.data?.images || response?.images || [];

      logger.info('Product images uploaded successfully', { count: images.length });
      return images;
    } catch (error) {
      logger.error('Failed to upload product images', error);
      throw error;
    }
  }

  /**
   * Delete a product image
   * @param url - Image URL or path to delete
   */
  async deleteProductImage(url: string): Promise<boolean> {
    try {
      logger.info('Deleting product image', { url });
      const response = await apiService.delete(`${API_PATHS.WEB_PRODUCTS}/images`, { data: { url } });
      return response?.success || false;
    } catch (error) {
      logger.error('Failed to delete product image', error);
      throw error;
    }
  }

  /**
   * Upload images for an existing product and save to product_images table
   * @param productId - Product ID to associate images with
   * @param files - Array of File objects to upload
   * @returns Array of uploaded image details with URLs
   */
  async uploadProductImagesForProduct(
    productId: string,
    files: File[]
  ): Promise<{ url: string; fileName: string; originalName: string; fileSize: number; mimeType: string }[]> {
    try {
      if (!files || files.length === 0) {
        throw new Error('No files provided');
      }

      logger.info('Uploading product images for product', { productId, count: files.length });

      const formData = new FormData();
      files.forEach((file) => {
        formData.append('images', file);
      });

      // Use uploadFile method for proper FormData handling
      const response = await apiService.uploadFile(
        `${API_PATHS.WEB_PRODUCTS}/${productId}/images`,
        formData
      );
      const images = response?.data?.images || response?.images || response?.data || [];

      logger.info('Product images uploaded successfully', { productId, count: images.length });
      return images;
    } catch (error) {
      logger.error('Failed to upload product images for product', error);
      throw error;
    }
  }

  /**
   * Get product images
   * @param productId - Product ID
   */
  async getProductImages(productId: string): Promise<any[]> {
    try {
      logger.info('Fetching product images', { productId });
      const response = await apiService.get(`${API_PATHS.WEB_PRODUCTS}/${productId}/images`);
      return response?.data || response || [];
    } catch (error) {
      logger.error('Failed to fetch product images', error);
      throw error;
    }
  }

  // ==================== STATISTICS ====================

  /**
   * Get product statistics from dedicated endpoint
   * Returns: total, active, inStock, outOfStock
   */
  async getStatistics(): Promise<{
    total: number;
    active: number;
    inactive: number;
    inStock: number;
    outOfStock: number;
  }> {
    try {
      logger.info('Fetching product statistics');
      const response = await apiService.get(API_PATHS.STATISTICS);
      const data = response?.data || response;
      return {
        total: data.total || 0,
        active: data.active || 0,
        inactive: data.inactive || (data.total - data.active) || 0,
        inStock: data.inStock || 0,
        outOfStock: data.outOfStock || 0,
      };
    } catch (error) {
      logger.error('Failed to fetch statistics', error);
      throw error;
    }
  }

  /**
   * Get product stats (extended) from stats endpoint
   * Returns: total, active, inactive, categoriesCount, newThisMonth
   */
  async getStats(): Promise<{
    total: number;
    active: number;
    inactive: number;
    categoriesCount: number;
    newThisMonth: number;
  }> {
    try {
      logger.info('Fetching product stats');
      const response = await apiService.get(API_PATHS.STATS);
      const data = response?.data || response;
      return {
        total: data.total || 0,
        active: data.active || 0,
        inactive: data.inactive || 0,
        categoriesCount: data.categoriesCount || 0,
        newThisMonth: data.newThisMonth || 0,
      };
    } catch (error) {
      logger.error('Failed to fetch stats', error);
      throw error;
    }
  }

  // ==================== BULK OPERATIONS ====================

  async bulkOperationProducts(operation: BulkProductOperation): Promise<any> {
    try {
      logger.info('Performing bulk operation on products', { operation });

      // Backend expects flat structure: { productIds, isActive, productStatus, categoryId, subCategoryId }
      const payload = {
        productIds: operation.productIds,
        ...operation.data,
      };

      const response = await apiService.post(`${API_PATHS.WEB_PRODUCTS}/bulk-update`, payload);
      return response?.data || response;
    } catch (error) {
      logger.error('Failed to perform bulk operation', error);
      throw error;
    }
  }

  // ==================== IMPORT/EXPORT OPERATIONS ====================

  async downloadProductTemplate(includeExamples: boolean = true): Promise<Blob> {
    try {
      logger.info('Downloading product template', { includeExamples });
      const params = { includeExamples };
      const response = await apiService.get(`${API_PATHS.WEB_PRODUCTS}/template/download`, { params });
      return response;
    } catch (error) {
      logger.error('Failed to download product template', error);
      throw error;
    }
  }

  async importProducts(
    file: File,
    options: {
      skipDuplicates?: boolean;
      updateExisting?: boolean;
      validateOnly?: boolean;
    } = {}
  ): Promise<any> {
    try {
      logger.info('Importing products', { fileName: file.name, options });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('skipDuplicates', String(options.skipDuplicates ?? false));
      formData.append('updateExisting', String(options.updateExisting ?? false));
      formData.append('validateOnly', String(options.validateOnly ?? false));

      const response = await apiService.post(`${API_PATHS.WEB_PRODUCTS}/import`, formData);
      return response?.data || response;
    } catch (error) {
      logger.error('Failed to import products', error);
      throw error;
    }
  }

  async exportProducts(params?: ProductExportParams): Promise<any> {
    try {
      logger.info('Exporting products', { params });

      const payload = {
        format: params?.format || 'xlsx',
        categoryId: params?.categoryId,
        status: params?.status,
        fields: params?.fields,
        fileName: params?.filename,
      };

      const response = await apiService.post(`${API_PATHS.WEB_PRODUCTS}/export`, payload);
      return response?.data || response;
    } catch (error) {
      logger.error('Failed to export products', error);
      throw error;
    }
  }

  // ==================== BACKGROUND JOB IMPORT/EXPORT (Email Notification) ====================

  /**
   * Download import template for products
   * @param importType - Type of import template to download
   */
  async downloadImportTemplate(importType: ProductImportType): Promise<void> {
    try {
      logger.info('Downloading import template', { importType });
      const response = await apiService.get(`${API_PATHS.IMPORT_EXPORT}/template/${importType}`, {
        responseType: 'blob',
      });

      // Create download link
      const blob = new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `product_import_template_${importType.toLowerCase()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('Failed to download import template', error);
      throw error;
    }
  }

  /**
   * Start background import job - results sent via email
   */
  async startImportJob(request: StartImportJobRequest): Promise<ImportExportJobResponse> {
    try {
      logger.info('Starting import job', { fileName: request.fileName, importType: request.importType });

      const response = await apiService.post(`${API_PATHS.IMPORT_EXPORT}/import`, {
        fileContent: request.fileContent,
        fileName: request.fileName,
        importType: request.importType,
        updateExisting: request.updateExisting,
        skipDuplicates: request.skipDuplicates,
        jobName: request.jobName,
      });

      const data = response?.data || response;
      return {
        jobId: data.jobId || data.id,
        status: data.status || 'queued',
        totalRows: data.totalRows,
        message: data.message,
      };
    } catch (error) {
      logger.error('Failed to start import job', error);
      throw error;
    }
  }

  /**
   * Export products - synchronous download, file returned directly as blob.
   */
  async startExportJob(request: StartExportJobRequest): Promise<{ success: true; fileName: string }> {
    try {
      logger.info('Starting export download', { exportType: request.exportType });

      const response = await apiService.post(
        `${API_PATHS.IMPORT_EXPORT}/export`,
        {
          exportType: request.exportType,
          format: request.format,
          filters: request.filters,
          selectedFields: request.selectedFields,
          jobName: request.jobName,
        },
        { responseType: 'blob' },
      );

      const blob = new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const sanitized = (request.jobName || 'products').replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `${sanitized}_${new Date().toISOString().split('T')[0]}.xlsx`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, fileName };
    } catch (error) {
      logger.error('Failed to export products', error);
      throw error;
    }
  }

  /**
   * Get import/export job status
   */
  async getJobStatus(jobId: string): Promise<any> {
    try {
      logger.info('Fetching job status', { jobId });
      const response = await apiService.get(`${API_PATHS.IMPORT_EXPORT}/jobs/${jobId}`);
      return response?.data || response;
    } catch (error) {
      logger.error('Failed to fetch job status', error);
      throw error;
    }
  }

  /**
   * Download the import report (Excel) for a completed/failed import job —
   * covers both failed rows AND duplicate-skipped rows (Status column
   * distinguishes them). Triggers a browser download; used by the
   * "Download Report" action on jobs with failedCount and/or skippedCount > 0.
   */
  async downloadImportErrorReport(jobId: string): Promise<void> {
    try {
      logger.info('Downloading import report', { jobId });
      const response = await apiService.get(`${API_PATHS.IMPORT_EXPORT}/jobs/${jobId}/error-report`, {
        responseType: 'blob',
      });

      const blob = new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `product-import-report-${jobId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('Failed to download import error report', error);
      throw error;
    }
  }

  /**
   * Get job history
   */
  async getJobHistory(params?: { type?: 'import' | 'export'; status?: string; page?: number; limit?: number }): Promise<any> {
    try {
      logger.info('Fetching job history', { params });
      const response = await apiService.get(`${API_PATHS.IMPORT_EXPORT}/jobs`, { params });
      return response?.data || response;
    } catch (error) {
      logger.error('Failed to fetch job history', error);
      throw error;
    }
  }

  /**
   * Cancel a pending job
   */
  async cancelJob(jobId: string): Promise<void> {
    try {
      logger.info('Cancelling job', { jobId });
      await apiService.post(`${API_PATHS.IMPORT_EXPORT}/jobs/${jobId}/cancel`);
    } catch (error) {
      logger.error('Failed to cancel job', error);
      throw error;
    }
  }

}

export const productService = new ProductService();
export default productService;
